import type { OrderLeg } from '../services/orders.service';
import { getDispatchScope } from './orderLegDiff';

// Stop-side field groups + inheritance helpers used when adding/removing legs.
//
// A "stop" is a single physical location with appointment, timing, and
// requirement data. Each leg has two stops: the origin (orig*) and the
// destination (dest*). When a leg is added or removed, stops move between
// legs, which is why the same field lists show up in multiple places (the
// structural change itself, the merge/clear logic, and the editedLegs
// reconciliation that drops edits whose target stop has moved away).

// --- Comprehensive side field lists -----------------------------------------
// Every dest-side field on a leg. "Boundary" fields (legmemo / legAction2 /
// legDrop) are associated with the dest side because they describe the gap
// between this leg's dest and the next leg's orig.
export const DEST_SIDE_FIELDS: ReadonlyArray<keyof OrderLeg> = [
  // location
  'destcode', 'destName', 'destCityState', 'destcoordinates',
  // appointment
  'destApptDate', 'destApptTime1', 'destApptTime2', 'destApptNumber',
  // timing
  'destArrivalDateTime', 'destDepartDateTime',
  'destgeoarrivaldatetime', 'destgeodepartdatetime',
  'destapptrequesteddatetime',
  // requirements + document recnos
  'destrequirepod', 'destrequireinterchange', 'destrequirescaleticket',
  'destpodrecno', 'destinterchangerecno', 'destscaleticketrecno',
  // boundary
  'legmemo', 'legAction2', 'legDrop',
];

// Subset used during a leg-merge (removing a stop): the surviving previous leg
// absorbs the deleted leg's destination. Requirements + document recnos are NOT
// carried across because the v1 rules engine recomputes them from the new leg
// structure on save. legstatus IS carried since it travels with the destination
// stop (e.g. a delivered destination keeps its delivered status post-merge).
export const DEST_MERGE_FIELDS: ReadonlyArray<keyof OrderLeg> = [
  'destcode', 'destName', 'destCityState', 'destcoordinates',
  'destApptDate', 'destApptTime1', 'destApptTime2', 'destApptNumber',
  'destArrivalDateTime', 'destDepartDateTime',
  'destgeoarrivaldatetime', 'destgeodepartdatetime',
  'destapptrequesteddatetime',
  'legmemo', 'legstatus', 'legAction2', 'legDrop',
];

// Mirror for the orig side. legtype + origmemo are the orig-side boundary
// fields (legtype mirrors the previous leg's legAction2).
export const ORIG_SIDE_FIELDS: ReadonlyArray<keyof OrderLeg> = [
  'origcode', 'origName', 'origCityState', 'origcoordinates',
  'origApptDate', 'origApptTime1', 'origApptTime2', 'origApptNumber',
  'origArrivalDateTime', 'origDepartDateTime',
  'origgeoarrivaldatetime', 'origgeodepartdatetime',
  'origapptrequesteddatetime',
  'origrequireinspection', 'origrequireinterchange', 'origrequirebol',
  'originspectionrecno', 'originterchangerecno', 'origbolrecno',
  'origmemo', 'legtype',
];

// Build an overlay copying the listed fields from src as-is. Used when one
// leg absorbs a side from another (e.g. dest-merge during stop removal) and
// the same field set drives the editedLegs cleanup that follows.
export const overlayFromLeg = <K extends keyof OrderLeg>(
  src: OrderLeg,
  fields: ReadonlyArray<K>,
): Pick<OrderLeg, K> => {
  const out: Partial<OrderLeg> = {};
  for (const f of fields) {
    out[f] = src[f];
  }
  return out as Pick<OrderLeg, K>;
};

// --- Edit-state helpers ----------------------------------------------------
// These operate on a `legs` array + an `editedLegs` map (legrecno → partial
// overlay) and let callers read/write the "effective" state — what the user
// currently sees on screen — independent of React state mechanics. They
// mirror the legacy "operate on current DOM values" pattern: edits live in a
// separate layer that overlays the underlying server values.

// Snapshot a leg with any pending edits applied. Used during structural
// changes so the resulting new/cleared legs reflect the user's unsaved
// edits, not stale underlying values.
export const effectiveLeg = (
  leg: OrderLeg,
  edits: Record<number, Partial<OrderLeg>>,
): OrderLeg => ({
  ...leg,
  ...(edits[leg.legrecno] || {}),
});

// Single-field variant of effectiveLeg. Returns the edited value if one
// exists for that field (even when intentionally empty), otherwise the
// underlying leg's value.
export const effectiveLegValue = <K extends keyof OrderLeg>(
  leg: OrderLeg,
  field: K,
  edits: Record<number, Partial<OrderLeg>>,
): OrderLeg[K] => {
  const e = edits[leg.legrecno];
  return e && field in e ? (e[field] as OrderLeg[K]) : leg[field];
};

// Smart setter for the editedLegs map: writes the edit when value differs
// from the leg's underlying value, removes it when they match (preventing
// no-op edit accumulation that would clutter the save payload's stale-data
// check). Mutates `updated` in place and returns it.
export const setLegFieldEdit = (
  updated: Record<number, Partial<OrderLeg>>,
  legrecno: number,
  field: keyof OrderLeg,
  value: unknown,
  legs: OrderLeg[],
): Record<number, Partial<OrderLeg>> => {
  const originalLeg = legs.find(l => l.legrecno === legrecno);
  const originalValue = originalLeg ? originalLeg[field] : undefined;
  const normalize = (v: unknown) => (v === null || v === undefined || v === '' ? '' : v);
  if (normalize(value) === normalize(originalValue)) {
    if (updated[legrecno]) {
      const legEdits = { ...updated[legrecno] };
      delete legEdits[field];
      if (Object.keys(legEdits).length === 0) {
        delete updated[legrecno];
      } else {
        updated[legrecno] = legEdits;
      }
    }
  } else {
    updated[legrecno] = {
      ...updated[legrecno],
      [field]: value as OrderLeg[keyof OrderLeg],
    };
  }
  return updated;
};

// Cascade the dispatch-scope-driven driver assignment when a leg's drop
// status toggles. Returns a new edits map with:
//   - The legDrop edit applied to the toggling leg (via setLegFieldEdit)
//   - For N → Y: drivers cleared from the legs that just left the scope
//     (the contiguous run from the next leg through the next pre-existing
//     drop), unless they're already dispatched (dispatchid != 0 or
//     dspdriverdatetime set — dispatcher-locked, can't silently undo).
//   - For Y → N: the toggling leg's driver copied onto any of those legs
//     whose driver is empty. Manually-assigned drivers are preserved
//     (don't overwrite a driver from when the legs were in a separate scope).
//   - No-op transitions (e.g. user re-selects the same value) skip the
//     cascade and just write the legDrop edit.
//
// dispatchid and dspdriverdatetime are never written so the user can freely
// toggle back and forth — auto-added drivers can be auto-removed because
// we never mark them dispatched.
export const cascadeLegDropChange = (
  legrecno: number,
  newValue: string,
  legs: OrderLeg[],
  currentEdits: Record<number, Partial<OrderLeg>>,
): Record<number, Partial<OrderLeg>> => {
  const updated = { ...currentEdits };
  const currentLegIndex = legs.findIndex(l => l.legrecno === legrecno);

  // Detect the actual transition before we apply the edit.
  const oldRaw = currentEdits[legrecno]?.legDrop
    ?? (currentLegIndex !== -1 ? legs[currentLegIndex].legDrop : undefined);
  const oldDrop = (oldRaw as string | undefined) || 'N';
  const newDrop = newValue || 'N';

  setLegFieldEdit(updated, legrecno, 'legDrop', newValue, legs);

  if (currentLegIndex === -1 || currentLegIndex >= legs.length - 1) return updated;
  if (oldDrop === newDrop) return updated;

  // Affected slice = next leg through the next pre-existing drop, using
  // effective legDrop on those legs so other pending drop edits are honored.
  const affected: OrderLeg[] = [];
  for (let i = currentLegIndex + 1; i < legs.length; i++) {
    affected.push(legs[i]);
    const otherDrop = (effectiveLegValue(legs[i], 'legDrop', updated) as string | undefined) || 'N';
    if (otherDrop === 'Y') break;
  }

  if (newDrop === 'Y') {
    affected.forEach(leg => {
      const dispatchIdNum = Number(effectiveLegValue(leg, 'dispatchid', updated)) || 0;
      const dspDateTime = String(effectiveLegValue(leg, 'dspdriverdatetime', updated) || '').trim();
      if (dispatchIdNum !== 0 || dspDateTime !== '') return;
      const currentDriver = String(effectiveLegValue(leg, 'driver', updated) || '').trim();
      if (currentDriver) setLegFieldEdit(updated, leg.legrecno, 'driver', '', legs);
    });
  } else {
    const sourceLeg = legs[currentLegIndex];
    const sourceDriver = String(effectiveLegValue(sourceLeg, 'driver', updated) || '').trim();
    if (sourceDriver) {
      affected.forEach(leg => {
        const currentDriver = String(effectiveLegValue(leg, 'driver', updated) || '').trim();
        if (!currentDriver) setLegFieldEdit(updated, leg.legrecno, 'driver', sourceDriver, legs);
      });
    }
  }

  return updated;
};

// Setting a DRIVERLESS leg Live (legDrop 'N') joins it to the following legs'
// dispatch group, and a group has one driver — so it takes theirs
// (bugfix/#34227; pros 5155623/24/31 were a chassis split set Live ahead of a
// driven pickup and left with no driver). cascadeLegDropChange only ever pushes
// the toggled leg's OWN driver forward, so it never fires here.
//
// Returns the driver — the first one AFTER the toggled leg in the merged group —
// and every driverless leg of that group, which includes any driverless legs
// already Live into the toggled one. Never overwrites an assigned driver. null
// when there is nothing to inherit.
//
// `legs` are the on-screen legs (pending-new ones included — an inserted split
// is the motivating case) and `edits` the staged overlay; the toggled leg is
// read as already carrying `newDrop`.
export const planLiveDriverInherit = (
  legrecno: number,
  newDrop: string,
  legs: OrderLeg[],
  edits: Record<number, Partial<OrderLeg>>,
): { driver: string; legrecnos: number[] } | null => {
  if ((newDrop || 'N') !== 'N') return null;
  const overlaid = legs.map((l) =>
    l.legrecno === legrecno
      ? { ...l, ...(edits[l.legrecno] || {}), legDrop: 'N' }
      : { ...l, ...(edits[l.legrecno] || {}) },
  );
  const byRecno = new Map(overlaid.map((l) => [l.legrecno, l]));
  const driverOf = (r: number) => String(byRecno.get(r)?.driver || '').trim();
  if (!byRecno.has(legrecno) || driverOf(legrecno)) return null;

  const scope = getDispatchScope(legrecno, overlaid);
  const source = scope.slice(scope.indexOf(legrecno) + 1).find((r) => driverOf(r));
  if (source === undefined) return null;
  return {
    driver: driverOf(source),
    legrecnos: scope.filter((r) => !driverOf(r)),
  };
};

// --- Inheritance helpers ----------------------------------------------------
// Each helper returns a Partial<OrderLeg> overlay describing one leg's stop
// (dest or orig) sourced from another leg's stop (dest or orig). Field policy:
//   - location:              always copied (same physical stop)
//   - appt + timing:         copied IFF linkTimes (a drop break in between
//                            would clear them)
//   - requirements + recnos: copied IFF includeRequirements (only when
//                            splitting an existing stop in place — i.e. mid-
//                            insert. For first/last, the new leg gets a fresh
//                            stop with no inherited requirements.)
// Memo fields (legmemo / origmemo) cross sides under specific structural
// rules and are handled by the caller, not the overlay.
export type StopOverlayOpts = {
  linkTimes: boolean;
  includeRequirements: boolean;
};

const ifLinked = (link: boolean, val: string | null | undefined) =>
  link ? (val ?? '') : '';

// New leg's dest = src leg's dest (e.g. mid-insert: split source's dest moves
// onto the new leg).
export const buildDestSideFromDest = (
  src: OrderLeg,
  opts: StopOverlayOpts,
): Partial<OrderLeg> => ({
  destcode: src.destcode,
  destName: src.destName,
  destCityState: src.destCityState,
  destcoordinates: src.destcoordinates,
  destApptDate: opts.linkTimes ? src.destApptDate : '',
  destApptTime1: ifLinked(opts.linkTimes, src.destApptTime1),
  destApptTime2: ifLinked(opts.linkTimes, src.destApptTime2),
  destApptNumber: ifLinked(opts.linkTimes, src.destApptNumber),
  destArrivalDateTime: ifLinked(opts.linkTimes, src.destArrivalDateTime),
  destDepartDateTime: ifLinked(opts.linkTimes, src.destDepartDateTime),
  destgeoarrivaldatetime: ifLinked(opts.linkTimes, src.destgeoarrivaldatetime),
  destgeodepartdatetime: ifLinked(opts.linkTimes, src.destgeodepartdatetime),
  destapptrequesteddatetime: ifLinked(opts.linkTimes, src.destapptrequesteddatetime),
  ...(opts.includeRequirements ? {
    destrequirepod: src.destrequirepod || 'N',
    destrequireinterchange: src.destrequireinterchange || 'N',
    destrequirescaleticket: src.destrequirescaleticket || 'N',
    destpodrecno: src.destpodrecno || 0,
    destinterchangerecno: src.destinterchangerecno || 0,
    destscaleticketrecno: src.destscaleticketrecno || 0,
  } : {}),
});

// New leg's dest = src leg's orig (e.g. first-insert: a new stop is added
// before leg1, so the new leg's destination is leg1's old origin).
export const buildDestSideFromOrig = (
  src: OrderLeg,
  opts: StopOverlayOpts,
): Partial<OrderLeg> => ({
  destcode: src.origcode,
  destName: src.origName,
  destCityState: src.origCityState,
  destcoordinates: src.origcoordinates,
  destApptDate: opts.linkTimes ? src.origApptDate : '',
  destApptTime1: ifLinked(opts.linkTimes, src.origApptTime1),
  destApptTime2: ifLinked(opts.linkTimes, src.origApptTime2),
  destApptNumber: ifLinked(opts.linkTimes, src.origApptNumber),
  destArrivalDateTime: ifLinked(opts.linkTimes, src.origArrivalDateTime),
  destDepartDateTime: ifLinked(opts.linkTimes, src.origDepartDateTime),
  destgeoarrivaldatetime: ifLinked(opts.linkTimes, src.origgeoarrivaldatetime),
  destgeodepartdatetime: ifLinked(opts.linkTimes, src.origgeodepartdatetime),
  destapptrequesteddatetime: ifLinked(opts.linkTimes, src.origapptrequesteddatetime),
  // Note: orig requirements (inspection/interchange/bol) are not auto-mapped
  // to dest requirements (pod/interchange/scaleticket) — different concepts.
  // Callers that need first/last requirement inheritance must do it themselves.
});

// New leg's orig = src leg's dest (e.g. last-insert: a new stop is added
// after the last leg, so the new leg's origin is the last leg's old dest).
export const buildOrigSideFromDest = (
  src: OrderLeg,
  opts: StopOverlayOpts,
): Partial<OrderLeg> => ({
  origcode: src.destcode,
  origName: src.destName,
  origCityState: src.destCityState,
  origcoordinates: src.destcoordinates,
  origApptDate: opts.linkTimes ? src.destApptDate : '',
  origApptTime1: ifLinked(opts.linkTimes, src.destApptTime1),
  origApptTime2: ifLinked(opts.linkTimes, src.destApptTime2),
  origApptNumber: ifLinked(opts.linkTimes, src.destApptNumber),
  origArrivalDateTime: ifLinked(opts.linkTimes, src.destArrivalDateTime),
  origDepartDateTime: ifLinked(opts.linkTimes, src.destDepartDateTime),
  origgeoarrivaldatetime: ifLinked(opts.linkTimes, src.destgeoarrivaldatetime),
  origgeodepartdatetime: ifLinked(opts.linkTimes, src.destgeodepartdatetime),
  origapptrequesteddatetime: ifLinked(opts.linkTimes, src.destapptrequesteddatetime),
});

// Dispatch fields (driver, dispatchid, donotpayleg, dspdriverdatetime) for a
// new leg that's being split off from an adjacent leg.
//   - dispatchid: always 0 — sr_driver mints a new legdispatch row on save
//   - driver / dspdriverdatetime: copied IFF linkDriver (a drop break ends
//     the dispatch scope, so a separated leg starts with no driver)
//   - donotpayleg: always copied (a per-leg flag, independent of scope)
export const carryDispatchAcrossSplit = (
  src: OrderLeg,
  opts: { linkDriver: boolean },
): Partial<OrderLeg> => ({
  driver: opts.linkDriver ? (src.driver || '') : '',
  dispatchid: 0,
  donotpayleg: src.donotpayleg || 'N',
  dspdriverdatetime: opts.linkDriver ? (src.dspdriverdatetime || '') : '',
});

// Reset a leg's dest side to empty/default values. Used when splitting a leg
// in place (mid-insert) — the source leg's dest data moves onto the new leg
// and the source's dest is cleared so the user can pick the new stop.
export const clearDestSide = (): Partial<OrderLeg> => ({
  destcode: 0,
  destName: '',
  destCityState: '',
  destcoordinates: '',
  destApptDate: '',
  destApptTime1: '',
  destApptTime2: '',
  destApptNumber: '',
  destArrivalDateTime: '',
  destDepartDateTime: '',
  destgeoarrivaldatetime: '',
  destgeodepartdatetime: '',
  destapptrequesteddatetime: '',
  destrequirepod: 'N',
  destrequireinterchange: 'N',
  destrequirescaleticket: 'N',
  destpodrecno: 0,
  destinterchangerecno: 0,
  destscaleticketrecno: 0,
  legmemo: '',
  legAction2: 'STOPOFF',
  legDrop: 'N',
});
